'use strict'

import type { ComponentProps } from 'react'

import Box from '@components/kit/box'
import Text from '@components/kit/text'
import { ChatMessage } from '@store/Chat/types'

export type BubbleTheme = 'default' | 'walrusAI' | 'failed'
export type BubbleAuthorType = 'sender' | 'sending' | 'recipient'

export type BubbleStyle = {
  bg: ComponentProps<typeof Box>['bg']
  textColor: ComponentProps<typeof Text>['textColor']
}

type MessageBubbleVariants = {
  [k in BubbleTheme]: {
    sender: BubbleStyle
    sending?: BubbleStyle
    recipient: BubbleStyle
  }
}

const messageBubbleVariants: MessageBubbleVariants = {
  default: {
    sender: { bg: 'walrusBlue700', textColor: 'white' },
    sending: { bg: 'newMutedPurple', textColor: 'shade05b' },
    recipient: { bg: 'walrusBlue050', textColor: 'newPurple' }
  },
  walrusAI: {
    sender: { bg: 'walrusRed050', textColor: 'walrusRed800' },
    recipient: { bg: 'walrusRed050', textColor: 'walrusRed800' }
  },
  failed: {
    sender: { bg: 'walrusRed050', textColor: 'walrusRed800' },
    recipient: { bg: 'walrusRed050', textColor: 'walrusRed800' }
  }
}

export function resolveBubbleTheme(
  message: Pick<ChatMessage, 'sendFailed' | 'walrus_ai'>
): BubbleTheme {
  if (message.sendFailed) {
    return 'failed'
  }
  if (message.walrus_ai) {
    return 'walrusAI'
  }
  return 'default'
}

export function resolveBubbleAuthorType(
  isSender: boolean,
  isSending: boolean
): BubbleAuthorType {
  if (!isSender) {
    return 'recipient'
  }
  return isSending ? 'sending' : 'sender'
}

export function getMessageBubbleStyle(
  theme: BubbleTheme,
  authorType: BubbleAuthorType
): BubbleStyle {
  const variant = messageBubbleVariants[theme]
  return variant[authorType] ?? variant.sender
}

const BUBBLE_RADIUS_PX = 20

export function getMessageBubbleBorderRadius(isSender: boolean) {
  return isSender
    ? {
        borderTopLeftRadius: BUBBLE_RADIUS_PX,
        borderTopRightRadius: BUBBLE_RADIUS_PX,
        borderBottomLeftRadius: BUBBLE_RADIUS_PX,
        borderBottomRightRadius: 0
      }
    : {
        borderTopLeftRadius: BUBBLE_RADIUS_PX,
        borderTopRightRadius: BUBBLE_RADIUS_PX,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: BUBBLE_RADIUS_PX
      }
}
